import {
  formatElapsedToHundredths,
  getElapsedMilliseconds,
  type StopwatchCommand,
} from "@hisani/stopwatch";
import { Button } from "@hisani/ui";
import { Check, ChevronDown, Clipboard, Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useId, useState } from "react";
import { browserClock } from "./stopwatch-browser";
import { StopwatchDistanceCombobox } from "./stopwatch-distance-combobox";
import { StopwatchSwipeToDelete } from "./stopwatch-swipe-to-delete";
import { StopwatchTimerControls } from "./stopwatch-timer-controls";
import {
  calculateSectionalSpeed,
  calculateSpeed,
  coveredDistanceMeters,
  formatAverageSpeed,
  formatAverageSpeedValues,
  statusMessage,
  type TimerViewModel,
} from "./stopwatch-workspace-model";

interface TimerCardProps {
  readonly timer: TimerViewModel;
  readonly index: number;
  readonly locale: string;
  readonly showDeleteHint: boolean;
  readonly onCommand: (id: string, command: StopwatchCommand) => void;
  readonly onCopy: () => void;
  readonly onShare: () => void;
  readonly onDelete: () => void;
  readonly isCopied: boolean;
  readonly onDistanceChange: (
    id: string,
    nextSplitDistanceMeters: number,
  ) => void;
  readonly onReset: (timer: TimerViewModel) => void;
  readonly onComment: (timer: TimerViewModel) => void;
  readonly onSave: (timer: TimerViewModel) => void;
  readonly isSaving: boolean;
  readonly saveDisabled: boolean;
}

export function StopwatchTimerCard({
  timer,
  index,
  locale,
  showDeleteHint,
  onCommand,
  onCopy,
  onShare,
  onDelete,
  isCopied,
  onDistanceChange,
  onReset,
  onComment,
  onSave,
  isSaving,
  saveDisabled,
}: TimerCardProps) {
  const t = useTranslations("Stopwatch");
  const [splitsOpen, setSplitsOpen] = useState(false);
  const timerNameId = useId();
  const splitsPanelId = useId();
  const splitsHeadingId = useId();
  const timerLabel =
    timer.run.label?.trim() || t("timerDefault", { number: index + 1 });
  const elapsedMilliseconds = getElapsedMilliseconds(timer.run, browserClock);
  const formattedElapsed = formatElapsedToHundredths(elapsedMilliseconds);
  const speed = calculateSpeed(timer, elapsedMilliseconds);
  const speedValues =
    speed === null ? null : formatAverageSpeedValues(speed, locale);
  const formattedSpeed =
    speed === null ? t("notAvailable") : formatAverageSpeed(speed, locale, t);
  const statusLabel = statusMessage(timer.run.status, t);
  const selectedDistance = timer.nextSplitDistanceMeters;
  const distanceFormatter = new Intl.NumberFormat(locale);
  const distanceCoveredMetersValue = coveredDistanceMeters(timer);
  const distanceCoveredFurlongs = t("distanceFurlongShort", {
    value: distanceFormatter.format(distanceCoveredMetersValue / 200),
  });
  const distanceCoveredMeters = t("distanceMeters", {
    value: distanceFormatter.format(distanceCoveredMetersValue),
  });
  const storyTarget: "timing" | undefined = index === 0 ? "timing" : undefined;

  return (
    <StopwatchSwipeToDelete
      deleteLabel={t("removeTimer", { label: timerLabel })}
      deleteText={t("remove")}
      isRtl={locale.startsWith("ar")}
      showHint={showDeleteHint}
      {...(storyTarget === undefined ? {} : { storyTarget })}
      onDelete={onDelete}
    >
      <article className="overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-[var(--elevation-1)]">
        <div className="px-3 @min-[40rem]/stopwatch:px-5">
          <div className="flex items-center gap-2 @min-[40rem]/stopwatch:gap-3">
            <div className="min-w-0 flex-1">
              <label className="sr-only" htmlFor={timerNameId}>
                {t("timerName", { number: index + 1 })}
              </label>
              <input
                id={timerNameId}
                value={timer.run.label ?? ""}
                placeholder={t("timerNamePlaceholder")}
                onChange={(event) =>
                  onCommand(timer.run.id, {
                    type: "RENAME",
                    label: event.target.value,
                  })
                }
                className="min-h-11 w-full rounded-md border-0 bg-transparent px-1 text-sm font-semibold text-primary outline-none placeholder:text-tertiary @min-[40rem]/stopwatch:text-base"
              />
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant={isCopied ? "accent" : "ghost"}
                size="sm"
                className="min-w-11 px-2"
                onClick={onCopy}
                aria-label={
                  isCopied
                    ? t("copiedTimer", { label: timerLabel })
                    : t("copyTimer", { label: timerLabel })
                }
              >
                {isCopied ? (
                  <Check
                    className="size-4 @min-[40rem]/stopwatch:me-1.5"
                    aria-hidden="true"
                  />
                ) : (
                  <Clipboard
                    className="size-4 @min-[40rem]/stopwatch:me-1.5"
                    aria-hidden="true"
                  />
                )}
                <span className="hidden @min-[40rem]/stopwatch:inline">
                  {isCopied ? t("copySuccess") : t("copy")}
                </span>
              </Button>
              <button
                type="button"
                onClick={onShare}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-secondary transition-colors hover:bg-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                aria-label={t("shareTimer", { label: timerLabel })}
              >
                <Share2 className="size-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <span className="sr-only" aria-live="polite">
            {statusLabel}
          </span>
        </div>

        <div className="px-3 pt-2 @min-[40rem]/stopwatch:px-5 @min-[40rem]/stopwatch:py-7">
          <div className="flex justify-center text-primary tabular-nums">
            <span
              className="font-sans text-[2.75rem] leading-none font-extrabold tracking-[-0.055em] @min-[40rem]/stopwatch:text-[clamp(3rem,13cqw,5.5rem)]"
              aria-label={t("elapsedTime", {
                time: formattedElapsed,
              })}
            >
              {formattedElapsed}
            </span>
          </div>

          <div
            className="mt-2 @min-[40rem]/stopwatch:mt-0"
            data-stopwatch-story-target={index === 0 ? "splits" : undefined}
          >
            <div className="mt-0 grid min-h-11 grid-cols-[27%_43%_30%] items-center bg-surface @min-[24rem]/stopwatch:grid-cols-[24%_52%_24%] @min-[40rem]/stopwatch:mt-4">
              <div
                className="flex min-h-11 min-w-0 items-center pe-1"
                role="group"
                aria-label={t("distanceCovered")}
              >
                <p className="flex items-baseline gap-0.5 whitespace-nowrap text-xs font-bold text-primary tabular-nums">
                  <span>{distanceCoveredFurlongs}</span>
                  <span className="text-tertiary" aria-hidden="true">
                    ·
                  </span>
                  <span className="text-secondary">
                    {distanceCoveredMeters}
                  </span>
                </p>
              </div>

              <div
                className="flex min-h-11 min-w-0 items-center justify-center border-x border-border-subtle px-1"
                role="group"
                aria-label={t("averageSpeed")}
                title={formattedSpeed}
              >
                {speedValues === null ? (
                  <p className="text-sm font-bold text-primary tabular-nums">
                    {t("notAvailable")}
                  </p>
                ) : (
                  <p className="flex min-w-0 flex-col items-center justify-center whitespace-nowrap text-xs leading-4 font-bold text-primary tabular-nums @min-[24rem]/stopwatch:flex-row @min-[24rem]/stopwatch:items-baseline">
                    <span>
                      {t("speedKilometersPerHour", {
                        value: speedValues.kilometersPerHour,
                      })}
                    </span>
                    <span
                      className="mx-px hidden text-tertiary @min-[24rem]/stopwatch:inline"
                      aria-hidden="true"
                    >
                      ·
                    </span>
                    <span className="text-secondary">
                      {t("speedMilesPerHour", {
                        value: speedValues.milesPerHour,
                      })}
                    </span>
                  </p>
                )}
              </div>

              <div className="min-w-0 ps-1 text-sm font-semibold text-primary">
                <StopwatchDistanceCombobox
                  value={selectedDistance}
                  ariaLabel={t("nextSplitDistanceFor", { label: timerLabel })}
                  onChange={(meters) => onDistanceChange(timer.run.id, meters)}
                />
              </div>
            </div>
          </div>

          <StopwatchTimerControls
            run={timer.run}
            splitDistanceMeters={selectedDistance}
            onCommand={onCommand}
            onReset={() => onReset(timer)}
            onComment={() => onComment(timer)}
            onSave={() => onSave(timer)}
            isSaving={isSaving}
            saveDisabled={saveDisabled}
            saveLabel={t("saveTimer", { label: timerLabel })}
          />

          <Button
            type="button"
            variant="ghost"
            className="-mx-3 mt-0 !min-h-10 w-[calc(100%+1.5rem)] justify-between rounded-none px-3 hover:bg-transparent active:bg-transparent @min-[40rem]/stopwatch:-mx-5 @min-[40rem]/stopwatch:w-[calc(100%+2.5rem)] @min-[40rem]/stopwatch:px-5"
            aria-controls={splitsPanelId}
            aria-expanded={splitsOpen}
            aria-label={t(
              splitsOpen ? "hideTimerDetails" : "showTimerDetails",
              { label: timerLabel },
            )}
            onClick={() => setSplitsOpen((open) => !open)}
          >
            {t("splitCount", {
              count: timer.run.sectionals.length,
            })}
            <ChevronDown
              className={`ms-2 size-4 transition-transform motion-reduce:transition-none ${
                splitsOpen ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </Button>

          <div
            id={splitsPanelId}
            className="pb-3 @min-[40rem]/stopwatch:pb-0"
            hidden={!splitsOpen}
          >
            <section
              className="pt-3 @min-[40rem]/stopwatch:mt-6 @min-[40rem]/stopwatch:pt-5"
              aria-labelledby={splitsHeadingId}
            >
              <div className="flex items-center justify-between gap-3">
                <h4
                  id={splitsHeadingId}
                  className="text-sm font-bold text-primary"
                >
                  {t("splits")}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-secondary">
                    {t("splitCount", { count: timer.run.sectionals.length })}
                  </span>
                </div>
              </div>

              {timer.run.sectionals.length === 0 ? (
                <p className="mt-2 rounded-md border border-dashed border-border-default bg-canvas px-3 py-3 text-center text-xs text-secondary @min-[40rem]/stopwatch:mt-3 @min-[40rem]/stopwatch:px-4 @min-[40rem]/stopwatch:py-5 @min-[40rem]/stopwatch:text-sm">
                  {t("noSplits")}
                </p>
              ) : (
                <div className="mt-2 max-h-36 overflow-y-auto overscroll-contain rounded-md border border-border-subtle @min-[40rem]/stopwatch:mt-3 @min-[40rem]/stopwatch:max-h-56">
                  <table className="w-full table-fixed border-collapse text-xs @min-[40rem]/stopwatch:text-sm">
                    <colgroup>
                      <col className="w-[9%]" />
                      <col className="w-[11%]" />
                      <col className="w-[26.666%]" />
                      <col className="w-[26.666%]" />
                      <col className="w-[26.666%]" />
                    </colgroup>
                    <thead className="sticky top-0 z-10 bg-surface">
                      <tr className="border-b border-border-default text-start text-xs tracking-[0.02em] text-secondary @min-[40rem]/stopwatch:tracking-[0.04em]">
                        <th
                          scope="col"
                          aria-label={t("splitNumber")}
                          className="py-2 ps-2 text-start font-bold @min-[40rem]/stopwatch:ps-4 @min-[40rem]/stopwatch:pe-2"
                        >
                          {t("splitNumberShort")}
                        </th>
                        <th
                          scope="col"
                          className="px-0.5 py-2 text-end font-bold @min-[40rem]/stopwatch:px-2"
                        >
                          {t("splitDistance")}
                        </th>
                        <th
                          scope="col"
                          className="px-1 py-2 text-end font-bold @min-[40rem]/stopwatch:px-2"
                        >
                          {t("split")}
                        </th>
                        <th
                          scope="col"
                          className="px-1 py-2 text-end font-bold @min-[40rem]/stopwatch:px-2"
                        >
                          {t("lastSegment")}
                        </th>
                        <th
                          scope="col"
                          className="py-2 ps-1 pe-2 text-end font-bold @min-[40rem]/stopwatch:ps-2 @min-[40rem]/stopwatch:pe-4"
                        >
                          {t("speed")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {timer.run.sectionals.map((sectional) => {
                        const sectionalSpeed = calculateSectionalSpeed(
                          sectional.distanceMeters,
                          sectional.splitMilliseconds,
                        );
                        const formattedSectionalSpeed =
                          sectionalSpeed === null
                            ? t("notAvailable")
                            : t("speedKilometersPerHour", {
                                value: formatAverageSpeedValues(
                                  sectionalSpeed,
                                  locale,
                                ).kilometersPerHour,
                              });

                        return (
                          <tr
                            key={sectional.sequence}
                            className="border-b border-border-subtle last:border-b-0"
                          >
                            <th
                              scope="row"
                              className="py-2 ps-2 text-start font-semibold text-primary @min-[40rem]/stopwatch:py-3 @min-[40rem]/stopwatch:ps-4 @min-[40rem]/stopwatch:pe-2"
                            >
                              {sectional.sequence}
                            </th>
                            <td className="px-0.5 py-2 text-end font-semibold text-primary @min-[40rem]/stopwatch:px-2 @min-[40rem]/stopwatch:py-3">
                              {t("distanceFurlongShort", {
                                value: sectional.distanceMeters / 200,
                              })}
                            </td>
                            <td className="px-1 py-2 text-end font-mono font-semibold text-primary tabular-nums @min-[40rem]/stopwatch:px-2 @min-[40rem]/stopwatch:py-3">
                              {formatElapsedToHundredths(
                                sectional.cumulativeMilliseconds,
                              )}
                            </td>
                            <td className="px-1 py-2 text-end font-mono text-secondary tabular-nums @min-[40rem]/stopwatch:px-2 @min-[40rem]/stopwatch:py-3">
                              {formatElapsedToHundredths(
                                sectional.splitMilliseconds,
                              )}
                            </td>
                            <td className="whitespace-nowrap py-2 ps-1 pe-2 text-end font-mono text-secondary tabular-nums @min-[40rem]/stopwatch:py-3 @min-[40rem]/stopwatch:ps-2 @min-[40rem]/stopwatch:pe-4">
                              {formattedSectionalSpeed}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      </article>
    </StopwatchSwipeToDelete>
  );
}
